import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Typography, Button, TextField, Box, Paper, IconButton, 
  Dialog, DialogTitle, DialogContent, DialogActions, Checkbox, 
  FormControlLabel } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { Alert } from '@mui/material';
import { useAuth } from '../auth/Auth';

const SuppliersPageCZ = () => {
  const [contractor, setContractor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errors, setErrors] = useState({
    syrveEdrpou: '',
    name: ''
  });
  const [generalError, setGeneralError] = useState('');
  const [suppliers, setSuppliers] = useState({});
  const [newSupplier, setNewSupplier] = useState({
    syrveEdrpou: '',
    name: '',
    contractorId: '',
    status: 1,
    account: ''
  });
  const [existingSupplierMode, setExistingSupplierMode] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingSupplier, setEditingSupplier] = useState(null);
  const { user } = useAuth();
  const [translations, setTranslations] = useState(null);
  const [multipleAccounts, setMultipleAccounts] = useState(['']);
  const [userRegion, setUserRegion] = useState('cs-CZ');

  useEffect(() => {
    if (user?.user_id) {
      fetchUserRegion();
    }
  }, [user]);

  const fetchUserRegion = async () => {
    try {
      const response = await axios.get(`https://bank2.daolog.net/user-region/${user.user_id}`);
      setUserRegion(response.data.region || 'cs-CZ');
      await fetchContractorData(response.data.region || 'cs-CZ');
    } catch (error) {
      console.error('Error fetching user region:', error);
      setUserRegion('cs-CZ');
      await fetchContractorData('cs-CZ');
    }
  };

  const fetchContractorData = async (region) => {
    try {
      const userId = user?.user_id;
      if (!userId) {
        // Тимчасово, поки не отримаємо переклади
        setGeneralError(region === 'uk-UA' ? 'Користувач не авторизований' : 
                       region === 'pl-PL' ? 'Użytkownik nie jest autoryzowany' : 
                       'Uživatel není autorizován');
        setLoading(false);
        return;
      }

      // Отримуємо переклади з сервера
      const translationsResponse = await axios.get(`https://bank2.daolog.net/translations/suppliers-cz/${region}`);
      setTranslations(translationsResponse.data);

      const contractorDetailsResponse = await axios.get(`https://bank2.daolog.net/contractor-details-mmm/${userId}`);
      const contractorData = contractorDetailsResponse.data;
      setContractor(contractorData);
      setNewSupplier(prev => ({...prev, contractorId: contractorData.id}));
      
      if (contractorData.id) {
        await fetchSuppliers(contractorData.id);
        await generateConfig(contractorData.id);
      }
      
      setLoading(false);
    } catch (error) {
      console.error('Error fetching contractor data:', error);
      const errorMessage = userRegion === 'uk-UA' ? 'Помилка при отриманні даних' : 
                          userRegion === 'pl-PL' ? 'Błąd podczas pobierania danych' : 
                          'Chyba při získávání dat';
      setGeneralError(errorMessage);
      setLoading(false);
    }
  };

  const generateConfig = async (contractorId) => {
    try {
      await axios.post(`https://bank2.daolog.net/generate-suppliers-config-mmm-cz-cz`, {
        contractorId: contractorId
      });
    } catch (error) {
      console.error('Error generating config:', error);
      const errorMessage = userRegion === 'uk-UA' ? 'Помилка при генерації конфігурації' : 
                          userRegion === 'pl-PL' ? 'Błąd podczas generowania konfiguracji' : 
                          'Chyba při generování konfigurace';
      setGeneralError(errorMessage);
    }
  };

  const fetchSuppliers = async (contractorId) => {
    try {
      const response = await axios.get(`https://bank2.daolog.net/suppliers-mmm-cz-cz/${contractorId}`);
      const data = response.data;
      
      // Коректно обробляємо статус
      const grouped = data.reduce((acc, supplier) => {
        if (!acc[supplier.syrve_edrpou]) {
          acc[supplier.syrve_edrpou] = [];
        }
        // Перетворюємо статус в число
        const supplierWithStatus = {
          ...supplier,
          status: parseInt(supplier.status) || 0
        };
        acc[supplier.syrve_edrpou].push(supplierWithStatus);
        return acc;
      }, {});
      
      setSuppliers(grouped);
    } catch (error) {
      console.error('Error fetching suppliers:', error);
      const errorMessage = userRegion === 'uk-UA' ? 'Помилка при отриманні постачальників' : 
                          userRegion === 'pl-PL' ? 'Błąd podczas pobierania dostawców' : 
                          'Chyba při získávání dodavatelů';
      setGeneralError(errorMessage);
    }
  };

  const handleExistingSupplierClick = (syrveEdrpou, name) => {
    setExistingSupplierMode(true);
    setNewSupplier(prev => ({
      ...prev,
      syrveEdrpou: syrveEdrpou,
      name: name
    }));
    setMultipleAccounts(['']);
  };

  const handleOpenEditDialog = (supplier) => {
    const supplierToEdit = { 
      ...supplier,
      oldSyrveEdrpou: supplier.syrve_edrpou,
      oldAccount: supplier.account,
      status: parseInt(supplier.status) || 1
    };
    setEditingSupplier(supplierToEdit);
    setEditDialogOpen(true);
  };

  const handleCloseEditDialog = () => {
    setEditingSupplier(null);
    setEditDialogOpen(false);
  };

  const handleStatusChange = async (syrveEdrpou, e) => {
    const newStatus = e.target.checked ? 1 : 0;
    
    try {
      const response = await axios.put(`https://bank2.daolog.net/suppliers/group-status-mmm-cz-cz/${syrveEdrpou}`, {
        status: newStatus,
        contractorId: contractor?.id
      });
      
      if (response.data.success) {
        // Оновлюємо локальний стан
        setSuppliers(prev => {
          const updated = { ...prev };
          if (updated[syrveEdrpou]) {
            updated[syrveEdrpou] = updated[syrveEdrpou].map(supplier => ({
              ...supplier,
              status: newStatus
            }));
          }
          return updated;
        });
        
        await generateConfig(contractor?.id);
      } else {
        await fetchSuppliers(contractor?.id);
      }
    } catch (error) {
      console.error('Error updating suppliers status:', error);
      const errorMessage = userRegion === 'uk-UA' ? 'Помилка при оновленні статусу постачальників' : 
                          userRegion === 'pl-PL' ? 'Błąd podczas aktualizacji statusu dostawców' : 
                          'Chyba při aktualizaci stavu dodavatelů';
      setGeneralError(errorMessage);
      await fetchSuppliers(contractor?.id);
    }
  };

  const handleAddAccount = () => {
    setMultipleAccounts([...multipleAccounts, '']);
  };

  const handleAccountChange = (index, value) => {
    const newAccounts = [...multipleAccounts];
    newAccounts[index] = value;
    setMultipleAccounts(newAccounts);
  };

  const handleRemoveAccount = (index) => {
    const newAccounts = multipleAccounts.filter((_, i) => i !== index);
    setMultipleAccounts(newAccounts);
  };

  const handleSaveEdit = async () => {
    try {
      const statusValue = parseInt(editingSupplier.status) || 1;
      
      const response = await axios.put(`https://bank2.daolog.net/suppliers-mmm-cz-cz/update/${editingSupplier.id}`, {
        ...editingSupplier,
        status: statusValue,
        contractorId: contractor?.id,
        syrve_edrpou: editingSupplier.syrve_edrpou,
        oldSyrveEdrpou: editingSupplier.oldSyrveEdrpou,
        oldAccount: editingSupplier.oldAccount
      });
      
      if (response.data.success) {
        await fetchSuppliers(contractor?.id);
        await generateConfig(contractor?.id);
        handleCloseEditDialog();
      }
    } catch (error) {
      console.error('Error updating supplier:', error);
      const errorMessage = userRegion === 'uk-UA' ? 'Помилка при оновленні постачальника' : 
                          userRegion === 'pl-PL' ? 'Błąd podczas aktualizacji dostawcy' : 
                          'Chyba při aktualizaci dodavatele';
      setGeneralError(errorMessage);
    }
  };

  const validateForm = () => {
    let isValid = true;
    const newErrors = {
      syrveEdrpou: '',
      name: ''
    };

    if (!existingSupplierMode && !newSupplier.syrveEdrpou.trim()) {
      newErrors.syrveEdrpou = translations?.warning || 'Toto pole je povinné';
      isValid = false;
    }
    if (!existingSupplierMode && !newSupplier.name.trim()) {
      newErrors.name = translations?.warning || 'Toto pole je povinné';
      isValid = false;
    }
    if (multipleAccounts.some(account => !account.trim())) {
      const errorMsg = userRegion === 'uk-UA' ? 'Всі рахунки повинні бути заповнені' : 
                      userRegion === 'pl-PL' ? 'Wszystkie konta muszą być wypełnione' : 
                      'Všechny účty musí být vyplněny';
      setGeneralError(errorMsg);
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleAddSupplier = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      const promises = multipleAccounts.map(account => 
        axios.post(`https://bank2.daolog.net/suppliers-mmm-cz-cz`, {
          ...newSupplier,
          account: account,
          contractorId: contractor?.id
        })
      );

      await Promise.all(promises);
      
      setNewSupplier({
        syrveEdrpou: '',
        name: '',
        contractorId: contractor?.id,
        status: 1,
        account: ''
      });
      setMultipleAccounts(['']);
      setErrors({
        syrveEdrpou: '',
        name: ''
      });
      setExistingSupplierMode(false);
      setGeneralError('');
      await fetchSuppliers(contractor?.id);
      await generateConfig(contractor?.id);
    } catch (error) {
      const errorMessage = error.response?.data?.message || 
        (userRegion === 'uk-UA' ? 'Помилка при додаванні постачальника' : 
         userRegion === 'pl-PL' ? 'Błąd podczas dodawania dostawcy' : 
         'Chyba při přidávání dodavatele');
      setGeneralError(errorMessage);
    }
  };

  const handleCancelAdd = () => {
    setNewSupplier({
      syrveEdrpou: '',
      name: '',
      contractorId: contractor?.id,
      account: ''
    });
    setMultipleAccounts(['']);
    setExistingSupplierMode(false);
    setErrors({
      syrveEdrpou: '',
      name: ''
    });
    setGeneralError('');
  };

  const handleDeleteSupplier = async (id) => {
    try {
      await axios.delete(`https://bank2.daolog.net/suppliers-mmm-cz-cz/${id}`, {
        data: {
          contractorId: contractor?.id
        }
      });
      await fetchSuppliers(contractor?.id);
      await generateConfig(contractor?.id);
    } catch (error) {
      console.error('Error deleting supplier:', error);
      const errorMessage = userRegion === 'uk-UA' ? 'Помилка при видаленні постачальника' : 
                          userRegion === 'pl-PL' ? 'Błąd podczas usuwania dostawcy' : 
                          'Chyba při mazání dodavatele';
      setGeneralError(errorMessage);
    }
  };

  const getLoadingText = () => {
    return userRegion === 'uk-UA' ? 'Завантаження...' : 
           userRegion === 'pl-PL' ? 'Ładowanie...' : 
           'Načítání...';
  };

  if (loading) return <Typography>{getLoadingText()}</Typography>;
  if (generalError) return <Typography color="error">{generalError}</Typography>;

  return (
    <Box sx={{ p: 3 }} style={{ marginTop: "120px" }}>
      <Typography variant="h6" gutterBottom>
        <b>{translations?.client || 'Клієнт'}:</b> {contractor?.name}
      </Typography>
    
      <Typography variant="h5" gutterBottom style={{ marginTop: "20px" }}>
        {translations?.management || 'Управління постачальниками'}
      </Typography>
      
      <Paper sx={{ p: 2, mb: 2 }}>
        {generalError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {generalError}
          </Alert>
        )}
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center',
          gap: 2 
        }}>
          <TextField
            required
            error={Boolean(errors.syrveEdrpou)}
            label={translations?.ederpou_syrve || 'ЄДРПОУ Syrve'}
            value={newSupplier.syrveEdrpou}
            onChange={(e) => {
              setNewSupplier({...newSupplier, syrveEdrpou: e.target.value});
              setErrors({...errors, syrveEdrpou: ''});
            }}
            disabled={existingSupplierMode}
            helperText={errors.syrveEdrpou}
            sx={{ width: '400px' }}
          />
          
          {multipleAccounts.map((account, index) => (
            <Box key={index} sx={{ display: 'flex', gap: 1, alignItems: 'center', width: '400px' }}>
              <TextField
                required
                label={translations?.account_confirmation || 'Підтвердження з виписки'}
                value={account}
                onChange={(e) => handleAccountChange(index, e.target.value)}
                sx={{ flex: 1 }}
              />
              {index === multipleAccounts.length - 1 ? (
                <IconButton onClick={handleAddAccount} color="primary">
                  <AddIcon />
                </IconButton>
              ) : (
                <IconButton onClick={() => handleRemoveAccount(index)} color="error">
                  <DeleteIcon />
                </IconButton>
              )}
            </Box>
          ))}

          <TextField
            required
            error={Boolean(errors.name)}
            label={translations?.name_contr || 'Назва постачальника'}
            value={newSupplier.name}
            onChange={(e) => {
              setNewSupplier({...newSupplier, name: e.target.value});
              setErrors({...errors, name: ''});
            }}
            disabled={existingSupplierMode}
            helperText={errors.name}
            sx={{ width: '400px' }}
          />
          
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleAddSupplier}
            >
              {translations?.add || 'Додати'}
            </Button>
            {existingSupplierMode && (
              <Button
                variant="outlined"
                onClick={handleCancelAdd}
              >
                {translations?.cancel || 'Скасувати'}
              </Button>
            )}
          </Box>
        </Box>
      </Paper>

      <Paper sx={{ p: 2 }}>
        {Object.entries(suppliers).map(([syrveEdrpou, supplierGroup]) => {
          const currentStatus = supplierGroup[0]?.status ?? 0;
          
          return (
            <Box key={syrveEdrpou} sx={{ mb: 2 }}>
              <Box 
                sx={{ 
                  p: 1, 
                  backgroundColor: 'rgba(0, 0, 0, 0.04)',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}
              >
                <Box 
                  sx={{ cursor: 'pointer' }}
                  onClick={() => handleExistingSupplierClick(syrveEdrpou, supplierGroup[0].name)}
                >
                  <Typography><b>{translations?.supplierName || 'ЄДРПОУ'}:</b> {syrveEdrpou}</Typography>
                  <Typography><b>{translations?.contrName || 'Постачальник'}:</b> {supplierGroup[0].name}</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={currentStatus === 1}
                        onChange={(e) => handleStatusChange(syrveEdrpou, e)}
                      />
                    }
                    label={currentStatus === 1 ? 
                      (translations?.activeConfig || 'Активна конфігурація') : 
                      (translations?.inactiveConfig || 'Неактивна конфігурація')}
                  />
                  <IconButton onClick={() => handleOpenEditDialog(supplierGroup[0])} color="primary">
                    <EditIcon />
                  </IconButton>
                </Box>
              </Box>
              <Box sx={{ pl: 2 }}>
                {supplierGroup.map((supplier) => (
                  <Box key={supplier.id} sx={{ display: 'flex', gap: 2, mb: 1, alignItems: 'center', mt: 1 }}>
                    <Typography sx={{ flex: 1 }}>
                      {supplier.account}
                    </Typography>
                    <IconButton onClick={() => handleDeleteSupplier(supplier.id)} color="error">
                      <DeleteIcon />
                    </IconButton>
                    <IconButton onClick={() => handleOpenEditDialog(supplier)} color="primary">
                      <EditIcon />
                    </IconButton>
                  </Box>
                ))}
              </Box>
            </Box>
          );
        })}
      </Paper>

      <Dialog open={editDialogOpen} onClose={handleCloseEditDialog}>
        <DialogTitle>
          {translations?.edit || 'Редагувати постачальника'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label={translations?.syrve_ederpou || 'ЄДРПОУ Syrve'}
              value={editingSupplier?.syrve_edrpou || ''}
              onChange={(e) => setEditingSupplier({...editingSupplier, syrve_edrpou: e.target.value})}
              fullWidth
            />
            <TextField
              label={translations?.name_contr || 'Назва постачальника'}
              value={editingSupplier?.name || ''}
              onChange={(e) => setEditingSupplier({...editingSupplier, name: e.target.value})}
              fullWidth
            />
            <TextField
              label={translations?.account_confirmation || 'Підтвердження з виписки'}
              value={editingSupplier?.account || ''}
              onChange={(e) => setEditingSupplier({...editingSupplier, account: e.target.value})}
              fullWidth
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={parseInt(editingSupplier?.status) === 1}
                  onChange={(e) => setEditingSupplier({
                    ...editingSupplier, 
                    status: e.target.checked ? 1 : 0
                  })}
                />
              }
              label={translations?.activeConfig || 'Активна конфігурація'}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEditDialog}>{translations?.cancel || 'Скасувати'}</Button>
          <Button onClick={handleSaveEdit} variant="contained">{translations?.save || 'Зберегти'}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SuppliersPageCZ;